import React, { useState, useEffect } from 'react';
import apiClient from '../../../utils/axios';

const AdminWatermark = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(true);

  // Check if user is admin (same logic as Navigation component)
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const response = await apiClient.get('/iam/me');

        const userData = response.data;
        
        // Check if user has admin access (same logic as existing components)
        const hasAdminRole = userData.role === 'admin';
        const isAdminTenant = userData.tenant_code === 'admin';
        setIsAdmin(hasAdminRole && isAdminTenant);
      } catch (err) {
        console.error('Admin status check failed:', err);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
    
    // Check periodically in case user changes
    const interval = setInterval(checkAdminStatus, 30000); // Check every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  // Add CSS for slide down animation only for banner
  useEffect(() => {
    if (isAdmin) {
      const style = document.createElement('style');
      style.textContent = `
        @keyframes adminWatermarkSlideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `;
      document.head.appendChild(style);
      
      return () => {
        document.head.removeChild(style);
      };
    }
  }, [isAdmin]);

  // Dispatch custom event when banner visibility changes
  useEffect(() => {
    if (isAdmin) {
      window.dispatchEvent(new CustomEvent('adminBannerVisibilityChanged', { 
        detail: { visible: bannerVisible } 
      }));
    } else {
      // Notify that banner should not be visible when user is not admin
      window.dispatchEvent(new CustomEvent('adminBannerVisibilityChanged', { 
        detail: { visible: false } 
      }));
    }
  }, [isAdmin, bannerVisible]);

  // Ensure we hide the banner when this component unmounts (e.g., navigating away)
  useEffect(() => {
    return () => {
      window.dispatchEvent(new CustomEvent('adminBannerVisibilityChanged', { 
        detail: { visible: false } 
      }));
    };
  }, []);

  // Don't render if not admin
  if (!isAdmin) {
    return null;
  }

  // Create repeating watermark pattern - opacity changes based on banner state
  const watermarkPatternStyle = {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    zIndex: 999, // Lower than banner but above content
    pointerEvents: 'none', // Allow clicks to pass through
    overflow: 'hidden',
    background: `
      repeating-linear-gradient(
        45deg,
        transparent,
        transparent 100px,
        rgba(229, 62, 62, ${bannerVisible ? '0.03' : '0.015'}) 100px,
        rgba(229, 62, 62, ${bannerVisible ? '0.03' : '0.015'}) 200px
      ),
      repeating-linear-gradient(
        -45deg,
        transparent,
        transparent 100px,
        rgba(229, 62, 62, ${bannerVisible ? '0.03' : '0.015'}) 100px,
        rgba(229, 62, 62, ${bannerVisible ? '0.03' : '0.015'}) 200px
      )
    `,
    transition: 'background 0.3s ease'
  };

  const watermarkGridStyle = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(`
      <svg xmlns='http://www.w3.org/2000/svg' width='300' height='200' viewBox='0 0 300 200'>
        <text x='50%' y='50%' 
              dominant-baseline='middle' 
              text-anchor='middle' 
              font-family='Arial, sans-serif' 
              font-size='18' 
              font-weight='bold' 
              fill='rgba(229, 62, 62, ${bannerVisible ? '0.16' : '0.10'})' 
              transform='rotate(-25 150 100)'>
          ADMIN MODE
        </text>
      </svg>
    `)}")`,
    backgroundRepeat: 'repeat',
    backgroundSize: '300px 200px',
    transition: 'opacity 0.3s ease'
  };

  const bannerStyle = {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    background: 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)',
    color: 'white',
    padding: '0.5rem 1rem',
    zIndex: 1001, // Higher than navigation and watermark
    fontSize: '0.875rem',
    fontWeight: '600',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    display: bannerVisible ? 'flex' : 'none',
    justifyContent: 'space-between',
    alignItems: 'center',
    animation: bannerVisible ? 'adminWatermarkSlideDown 0.3s ease' : 'none'
  };

  const dismissButtonStyle = {
    background: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    color: 'white',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'background 0.2s ease'
  };

  return (
    <>
      {/* Repeating watermark pattern - always visible when admin */}
      <div style={watermarkPatternStyle}>
        <div style={watermarkGridStyle} />
      </div>
      
      {/* Warning banner - can be dismissed */}
      <div style={bannerStyle}>
        <div style={{ flex: 1 }}>
          ⚠️ ADMIN MODE ACTIVE - You have elevated privileges. Exercise extreme caution with all actions.
        </div>
        <button 
          style={dismissButtonStyle}
          onClick={() => setBannerVisible(false)}
          onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
          onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
        >
          Dismiss
        </button>
      </div>
    </>
  );
};

export default AdminWatermark; 